import { useDialog } from '@globals/dialog/useDialogStore'
import { useApiActionToast } from '@globals/toaster/useToasterStore'
import { AwardType, NominateParams, listCandidates, nominate } from '@kudos/http-client'
import Stack from '@mui/material/Stack'
import { useApiError } from '@shared/errors/useApiError'
import Cancel from '@shared/forms/Cancel'
import FormContextWrapper from '@shared/forms/FormContextWrapper'
import Submit from '@shared/forms/Submit'
import TextAreaField from '@shared/forms/TextAreaField'
import {
  SearchResults,
  TenantMemberSearchFn,
} from '@shared/forms/tenantMemberAndTeamSearchField/useSearchTenantMembersAndTeams'
import TenantMemberSearchField from '@shared/forms/tenantMemberSearchField/TenantMemberSearchField'
import { useInvalidateQuery } from '@shared/hooks/useInvalidateQuery'
import useCanRefineMessage from '@shared/refinement/useCanRefineMessage'
import { useMutation } from '@tanstack/react-query'
import { PINNED_TIMELINE_ITEMS_QK } from '@tenant/home/user/upcomingAndImportantSection/usePinnedTimelineItems'
import { T, useT } from '@transifex/react'
import { ReactNode, useEffect } from 'react'
import { FieldValues, UseFormSetError } from 'react-hook-form'
import { useShallow } from 'zustand/shallow'
import useMyAwards from '../useMyAwards'
import RefineNominationResponseButton from './RefineNominationResponseButton'
import { SEND_NOMINATIONS_MODAL_KEY } from './SendNominationsModal'
import useNominateForm, { MAX_NOM_RESPONSE_CHARS, NominateFormData } from './useNominateForm'
import { useSendNominationFormStore } from './useSendNominationFormStore'

type NominateFormProps = {
  nomination: AwardType
  isPinned?: boolean
}

const NominateForm: React.FC<NominateFormProps> = ({ nomination, isPinned }) => {
  const invalidateQuery = useInvalidateQuery()
  const { refetch } = useMyAwards(['attachmentUrls', 'ownNominees'], false)
  const [showSuccess] = useApiActionToast()
  const { handleApiError } = useApiError()
  const t = useT()
  const { closeDialog: closeNominationDialog } = useDialog(SEND_NOMINATIONS_MODAL_KEY)
  const { setIsFormDirty } = useSendNominationFormStore(
    useShallow(state => ({
      setIsFormDirty: state.setIsFormDirty,
    })),
  )
  const { methods } = useNominateForm(nomination.questions)
  const { showRefineButton } = useCanRefineMessage()

  useEffect(() => {
    setIsFormDirty(methods.formState.isDirty)
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.formState.isDirty])

  const { mutateAsync: createNomination } = useMutation({
    mutationFn: (data: NominateParams) => nominate(nomination.id, data),
    onSuccess: async () => {
      if (isPinned) {
        await invalidateQuery([PINNED_TIMELINE_ITEMS_QK])
      } else {
        refetch()
      }
      closeNominationDialog()
      showSuccess(t('Nomination sent successfully'))
    },
    onError: error =>
      handleApiError(error, methods.setError as UseFormSetError<FieldValues>, [
        'nominee',
        'message',
      ]),
  })

  const onSubmit = async (formData: NominateFormData) => {
    const responses =
      nomination?.questions?.map((question, index) => ({
        questionId: question.id,
        valueString: formData.responses?.[index]?.valueString,
        refinementId: formData.responses?.[index]?.refinementId ?? null,
      })) ?? []

    await createNomination({
      nomineeId: formData.nominee[0].id,
      responses,
    })
  }

  const searchFn: TenantMemberSearchFn = async ({ search }) => {
    const data = await listCandidates(nomination.id, {
      search,
    })
    return data as unknown as SearchResults
  }

  const FormActions: ReactNode = (
    <>
      <Cancel elementId="btnNominateFormCancel" onClick={closeNominationDialog} />
      <Submit
        elementId="btnNominate"
        formState={methods.formState}
        submitText={<T _str="Submit" />}
      />
    </>
  )

  return (
    <FormContextWrapper
      isInDialog
      formMethods={methods}
      onSubmit={onSubmit}
      formActions={FormActions}>
      <TenantMemberSearchField
        name="nominee"
        control={methods.control}
        label={<T _str="Choose someone to nominate" />}
        help={t('Ineligible users and users you’ve already nominated will not show')}
        searchFn={searchFn}
        zIndexPlacement="modal"
      />
      <Stack
        useFlexGap
        spacing={2}
        sx={{
          mt: 2,
          pb: { xs: 2, sm: 6 },
        }}>
        <Stack useFlexGap spacing={1}>
          {nomination.questions &&
            nomination.questions.map((question, index) => (
              <TextAreaField
                key={question.id}
                name={`responses.${index}.valueString`}
                control={methods.control}
                label={question.name}
                maxChars={MAX_NOM_RESPONSE_CHARS}
                labelStyle={{ textWrap: 'wrap' }}
                refineButton={
                  showRefineButton && <RefineNominationResponseButton responseIndex={index} />
                }
              />
            ))}
        </Stack>
      </Stack>
    </FormContextWrapper>
  )
}

export default NominateForm
